import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, Text} from '@forecast-it/design-system';
import {StudioAgentEntry, StudioCatalogue} from './useNovaStudioShell';
import {NovaStudioColor} from '../shared/novaStudioColors';

// Forecast magenta — the left-accent bar and highlight on the hovered/active agent row. The design
// system has no semantic theme token for this brand pink (it only survives in the deprecated
// palette), and this left panel is deliberately hand-rolled rather than built from DS list
// components, so it lives here as a single named constant.
const ACCENT = '#E50085';
const ACCENT_TINT = '#fdeef6';
const ACCENT_TINT_HOVER = '#faf5f9';

const Panel = styled.div`
	width: 280px;
	border-right: 1px solid ${NovaStudioColor.border};
	display: flex;
	flex-direction: column;
	min-height: 0;
`;
const TopBar = styled.div`
	padding: 16px;
	flex-shrink: 0;
`;
// Only the agent list scrolls; TopBar (New agent) stays pinned above it.
const ScrollList = styled.div`
	flex-grow: 1;
	min-height: 0;
	overflow-y: auto;
`;
// Shown when the whole portfolio is empty: a centered prompt pointing to New agent.
const EmptyPortfolio = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 24px;
	text-align: center;
	color: ${NovaStudioColor.textMuted};
`;
const CountBadge = styled.span`
	min-width: 20px;
	padding: 1px 7px;
	border-radius: 10px;
	background: ${NovaStudioColor.badgeBackground};
	color: ${NovaStudioColor.textMuted};
	font-size: 11px;
	font-weight: 600;
	text-align: center;
`;
const CategoryHeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 16px;
`;
const CategoryRight = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const CategoryName = styled.span`
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.02em;
	color: ${NovaStudioColor.textMuted};
`;
// Active/hover are shown with a left-accent bar (and a faint tint) rather than a full background
// fill. The 3px left border is always present but transparent when idle, so the label never shifts.
const AgentRow = styled.button<{$selected: boolean}>`
	display: block;
	width: 100%;
	text-align: left;
	padding: 8px 16px;
	border: none;
	border-left: 3px solid ${props => (props.$selected ? ACCENT : 'transparent')};
	cursor: pointer;
	font-size: 14px;
	font-weight: ${props => (props.$selected ? 600 : 400)};
	color: ${props => (props.$selected ? NovaStudioColor.agentRowTextSelected : NovaStudioColor.agentRowText)};
	background: ${props => (props.$selected ? ACCENT_TINT : 'transparent')};

	&:hover {
		border-left-color: ${ACCENT};
		background: ${props => (props.$selected ? ACCENT_TINT : ACCENT_TINT_HOVER)};
	}
`;

export interface DeleteCategoryPayload {
	categoryId: string;
	categoryName: string;
	agentCount: number;
}

interface Props {
	catalogue: StudioCatalogue;
	selectedAgentId: string | null;
	onSelectAgent: (agentId: string) => void;
	onCreateAgent: () => void;
	onDeleteCategory: (payload: DeleteCategoryPayload) => void;
}

export function NovaStudioCatalogue({
	catalogue,
	selectedAgentId,
	onSelectAgent,
	onCreateAgent,
	onDeleteCategory,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	// The empty state replaces the catalogue only when the portfolio is truly pristine: no agents and
	// no user-created categories (just the empty Uncategorised group). As soon as the author has any
	// agent, or has created any category, the grouped list is shown instead — an empty *category*
	// still needs its heading (with a 0 badge) so it can be seen and deleted, even before any agent
	// exists. Both halves are positive checks over what actually exists, so an empty `groups` array
	// can't assert "no agents" on its own.
	const hasAnyAgent = catalogue.groups.some(group => group.agents.length > 0);
	const hasUserCategory = catalogue.groups.some(group => !group.category.isUncategorised);
	const isEmpty = !hasAnyAgent && !hasUserCategory;
	return (
		<Panel data-cy="nova-studio-catalogue">
			<TopBar>
				<Button
					emphasis="high"
					fullWidth
					icon="plus"
					data-cy="nova-new-agent"
					data-testid="nova-new-agent"
					onPress={onCreateAgent}
				>
					{formatMessage({id: 'nova.catalogue.newAgentShort', defaultMessage: 'New agent'})}
				</Button>
			</TopBar>
			{isEmpty ? (
				<EmptyPortfolio data-cy="nova-studio-catalogue-empty">
					<Text>{formatMessage({id: 'nova.catalogue.emptyTitle', defaultMessage: 'No agents yet'})}</Text>
					<Text>
						{formatMessage({
							id: 'nova.catalogue.emptyBody',
							defaultMessage: 'Create your first agent to get started.',
						})}
					</Text>
					<Button emphasis="high" icon="plus" data-cy="nova-studio-catalogue-empty-new-agent" onPress={onCreateAgent}>
						{formatMessage({id: 'nova.catalogue.newAgentShort', defaultMessage: 'New agent'})}
					</Button>
				</EmptyPortfolio>
			) : (
				<ScrollList data-cy="nova-studio-catalogue-scroll">
					{catalogue.groups.map(group => (
						<div key={group.category.categoryId} data-cy={`nova-category-${group.category.categoryId}`}>
							<CategoryHeaderRow>
								<CategoryName>{group.category.name}</CategoryName>
								<CategoryRight>
									<CountBadge>{group.agents.length}</CountBadge>
									{!group.category.isUncategorised && (
										<Button
											emphasis="low"
											icon="trash"
											iconOnly
											aria-label={formatMessage({
												id: 'nova.catalogue.deleteCategory',
												defaultMessage: 'Delete category',
											})}
											data-cy={`nova-delete-category-${group.category.categoryId}`}
											onPress={() =>
												onDeleteCategory({
													categoryId: group.category.categoryId,
													categoryName: group.category.name,
													agentCount: group.agents.length,
												})
											}
										/>
									)}
								</CategoryRight>
							</CategoryHeaderRow>
							{group.agents.map((agent: StudioAgentEntry) => (
								<AgentRow
									key={agent.agentId}
									$selected={agent.agentId === selectedAgentId}
									data-cy="nova-studio-agent-row"
									onClick={() => onSelectAgent(agent.agentId)}
								>
									{agent.name}
								</AgentRow>
							))}
						</div>
					))}
				</ScrollList>
			)}
		</Panel>
	);
}
